import React from "react";

/**
 * Spinner
 *
 * Four loaders, all from framework.scss. Pick by weight, not by taste:
 *
 *   ring      (default)  brand conic ring. The safe choice — reads as the
 *                        mark at every size and works inline.
 *   counter              ring with the B in the counter. Wordier; use when
 *                        the loader is the only thing on screen.
 *   sequence             three marks lighting in turn. Fills horizontal
 *                        space, so it suits a table row or card footer.
 *   minimal              plain bordered ring, no brand. For dense UI where
 *                        a logo would be noise — inline in a field, a cell.
 *
 * Sizes map to --loader-size: sm 18 · md 32 · lg 64 · xl 96.
 *
 * Below 28px the B is dropped and the ring carries the mark alone, matching
 * the logo sheet's small-size rule — so `counter` at `sm` renders as `ring`
 * rather than an unreadable glyph.
 */

const VARIANTS = ["ring", "counter", "sequence", "minimal"];
const SIZES = ["sm", "md", "lg", "xl"];

/* md is the SCSS default, so it needs no class. */
const sizeClass = (size) => (size && size !== "md" ? ` ${size}` : "");

function Loader({ variant, size, onLight }) {
  const light = onLight ? " on-light" : "";

  switch (variant) {
    case "counter":
      /* sm has no room for the glyph — fall through to the bare ring. */
      return <span className={`lpb-loader${sizeClass(size)}${light}`}>{size !== "sm" && <span className="lpb-loader-counter">B</span>}</span>;

    case "sequence":
      return (
        <span className={`lpb-loader-seq${sizeClass(size)}${light}`}>
          <span />
          <span />
          <span />
        </span>
      );

    case "minimal":
      return <span className={`lpb-loader-minimal${sizeClass(size)}`} />;

    case "ring":
    default:
      return <span className={`lpb-loader${sizeClass(size)}${light}`} />;
  }
}

export default function Spinner({ show = true, variant = "ring", size = "md", overlay = true, message, onLight = false, label = "Loading", className = "" }) {
  if (!show) return null;

  const safeVariant = VARIANTS.includes(variant) ? variant : "ring";
  const safeSize = SIZES.includes(size) ? size : "md";

  /* One live region on the outermost node — nesting role="status" inside
     role="status" makes screen readers announce twice. */
  const loader = (
    <span className={`lpb-loader-block ${className}`.trim()} role="status" aria-live="polite" aria-label={message ? undefined : label}>
      <Loader variant={safeVariant} size={safeSize} onLight={onLight} />
      {message && <span className="lpb-loader-message">{message}</span>}
    </span>
  );

  if (!overlay) return loader;

  return <div className="lpb-loader-overlay">{loader}</div>;
}
